import { saveAs } from 'file-saver';
import * as XLSX from 'xlsx';
import { SupplierLadderPriceAdd, SupplierPrice, SupplierPriceAdd } from '@/types/purchase';
import { dp, divided, noRateAmount } from '@/utils/calc';
import { DictLike } from '@/types/dict';
import { TFunction } from 'i18next';
import { strToTime } from '@/utils/strToTime';
import { dateFormat, priceFormat } from '@/utils/format';
import dayjs from 'dayjs';
import { notification } from 'antd';

/**
 * 获取供应商价格导入模板表头
 */
export const getSupplierPriceHeaders = (t: TFunction, multiSupp: boolean) => {
  let res = [
    t('purchase.excelHeaderInventoryCode'),
    t('purchase.excelHeaderUnitPriceIncludingTax'),
    t('purchase.taxRate'),
    t('purchase.effectiveAt'),
    t('purchase.expiredAtEcl'),
    t('purchase.excelHeaderSupplyCycle'),
    t('purchase.excelHeaderMinimumPackage'),
    t('purchase.excelHeaderQuantity'),
    t('purchase.excelHeaderSettlementCurrency'),
    t('purchase.excelHeaderBrand'),
    t('purchase.excelHeaderMpn'),
    t('purchase.excelHeaderShelfLife'),
    t('purchase.netWeight'),
    t('purchase.grossWeight'),
  ];
  if (multiSupp) res.push(t('purchase.code'))
  return res;
};

/**
 * 生成供应商价格导入模板并下载
 */
export const generateSupplierPriceTemplate = (t: TFunction, multiSupp: boolean) => {
  // 获取表头
  const headers = getSupplierPriceHeaders(t, multiSupp);

  // 工作表数据（仅表头）
  const worksheetData = [headers];

  // 创建工作表
  const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);

  const cols = [
    { wch: 20 },  // 存货编码
    { wch: 15 },  // 含税单价
    { wch: 15 },  // 税率
    { wch: 15 },  // 生效日期
    { wch: 15 },  // 失效日期
    { wch: 15 },  // 供货周期(天)
    { wch: 12 },  // 最小包装
    { wch: 12 },  // 数量
    { wch: 12 },  // 结算币种
    { wch: 15 },  // 品牌
    { wch: 15 },  // 原厂代码
    { wch: 15 },  // 保质期（年）
    { wch: 15 },  // 净重
    { wch: 15 },  // 毛重
  ];
  if (multiSupp) cols.push({ wch: 15 }) //供应商编码

  // 设置列宽
  worksheet['!cols'] = cols;

  // 创建工作簿
  const workbook = XLSX.utils.book_new();
  const sheetName = t('purchase.priceTemplateFileName');
  XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);

  // 生成Excel文件并下载
  const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
  const dataBlob = new Blob([excelBuffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8',
  });

  const fileName = `${t('purchase.priceTemplateFileName')}.xlsx`;
  saveAs(dataBlob, fileName);
};

/**
 * 解析上传的供应商价格Excel文件
 */
export interface SupplierPriceTemple {
  productCode: string;    //存货编码
  sPrice: string;         //含税单价
  taxRate: string;         //税率
  effectiveAt: string;      //生效日期
  expiredAt: string;      //失效日期
  days: string;           //供货周期（天）
  mpq: string;            //最小包装
  count: string;          //数量
  currency: string;       //结算币种
  brand: string;          //品牌
  mpn: string;            //原厂代码
  dcc: string;      //保质期（年）
  supplierCode?: string;
  netWeight: string;          //净重
  grossWeight: string;          //毛重
}

/**
 * 处理后的供应商价格数据
 */
// export interface ProcessedSupplierPrice {
//   productId: string;       // 存货编码
//   price: string;           // 含税单价
//   expiredAt: number;       // 物料有效期（年）
//   days: number | null;     // 供货周期（天）
//   mpq: string | null;      // 最小包装
//   currency: string;        // 结算币种（替换后的key）
//   ladders: SupplierLadderPriceAdd[];  // 阶梯价格
// }

/**
 * 获取单元格值并trim
 */
const getCellValue = (row: any[], index: number): string => {
  if (row[index] === undefined || row[index] === null) return '';
  return String(row[index]).trim();
};

export const parseSupplierPriceFile = (file: File, t: TFunction, multiSupp: boolean): Promise<SupplierPriceTemple[]> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = e.target?.result;
        const workbook = XLSX.read(data, { type: 'array' });
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];

        // 解析为二维数组
        const jsonData = XLSX.utils.sheet_to_json<string[]>(worksheet, { header: 1 });

        if (!jsonData || jsonData.length < 2) {
          reject(new Error('invalid'));
          return;
        }

        // 验证表头
        const headers = jsonData[0];
        const expectedHeaders = getSupplierPriceHeaders(t, multiSupp);
        const isValidHeader = expectedHeaders.every((header, index) => header === headers[index]);
        if (!isValidHeader) {
          reject(new Error('invalid'));
          return;
        }

        // 解析数据行（跳过表头）
        const result: SupplierPriceTemple[] = [];
        for (let i = 1; i < jsonData.length; i++) {
          const row = jsonData[i];
          if (!row || row.length === 0) continue;
          let supplierCode = getCellValue(row, expectedHeaders.length - 1);
          if (!!!supplierCode && multiSupp) {
            notification.error({ message: t('purchase.supplierCodeNeed', { index: i + 1 }) })
            resolve([])
            return;
          }
          let o: number = 0;
          result.push({
            productCode: getCellValue(row, o),
            sPrice: getCellValue(row, ++o),
            taxRate: getCellValue(row, ++o),
            effectiveAt: getCellValue(row, ++o),
            expiredAt: getCellValue(row, ++o),
            days: getCellValue(row, ++o),
            mpq: getCellValue(row, ++o),
            count: getCellValue(row, ++o),
            currency: getCellValue(row, ++o).toUpperCase(),
            brand: getCellValue(row, ++o),
            mpn: getCellValue(row, ++o),
            dcc: getCellValue(row, ++o),
            supplierCode: multiSupp ? supplierCode : undefined,
            netWeight: getCellValue(row, ++o),
            grossWeight: getCellValue(row, ++o)
          });
        }

        resolve(result);
      } catch {
        reject(new Error('invalid'));
      }
    };
    reader.onerror = () => reject(new Error('invalid'));
    reader.readAsArrayBuffer(file);
  });
};

type SupplierPriceAddWithCode = SupplierPriceAdd & { supplierCode?: string };
/**
 * 处理供应商价格数据，按物料编码分组并合并阶梯价格
 * @param data 解析后的原始数据
 * @returns 处理后的供应商价格数据数组（currency字段仍为原始值，需要后续处理）
 */
export const processSupplierPriceData = (data: SupplierPriceTemple[]): SupplierPriceAddWithCode[] => {
  // 按物料编码分组
  const grouped = new Map<string, SupplierPriceTemple[]>();
  for (const item of data) {
    let key = `${item.productCode}->S${item.supplierCode ?? ''}`
    if (!item.productCode) continue;
    if (!grouped.has(key)) {
      grouped.set(key, []);
    }
    grouped.get(key)!.push(item);
  }

  const result: SupplierPriceAddWithCode[] = [];

  Array.from(grouped.entries()).forEach(([key, items]) => {
    // 第一条是基础价格，包含完整信息
    const first = items[0];
    const basePrice = first.sPrice;

    // 构建阶梯价格（从第二条开始）
    const ladders: SupplierLadderPriceAdd[] = [
      {
        count: 1,
        price: basePrice ?? '',
        discount: '100',
        remark: '',
      }
    ];
    for (let i = 1; i < items.length; i++) {
      const item = items[i];
      if (item.count && item.sPrice) {
        // 计算折扣率：当前阶梯价格 / 基础价格，保留两位小数
        const discount = dp(divided(item.sPrice, basePrice));
        ladders.push({
          count: Number(item.count),
          price: item.sPrice,
          discount: discount,
          remark: '',
        });
      }
    };

    let [productCode, supplierCode] = key.split('->S');

    result.push({
      productId: productCode ?? '',
      price: basePrice,
      effectiveAt: first.effectiveAt ? strToTime(first.effectiveAt) : 0,
      expiredAt: first.expiredAt ? strToTime(first.expiredAt) : 0,
      days: first.days ? Number(first.days) : null,
      mpq: first.mpq || null,
      currency: first.currency,
      ladders,
      remark: "",
      brand: first.brand || null,
      mpn: first.mpn || null,
      dcc: first.dcc ?? '',
      supplierCode: supplierCode ?? '',
      taxRate: first.taxRate ?? undefined,
      netWeight: first.netWeight || null,
      grossWeight: first.grossWeight || null,
    });
  });

  return result;
};

/**
 * 批量替换数据中的 currency 字段
 * @param data 包含 currency 字段的数据数组
 * @param currencys 币种字典
 * @returns 替换后的数据，类型与输入一致
 */
export const replaceCurrencyKeys = <T extends { currency?: string }>(
  data: T[],
  currencys: DictLike[],
  type: '1' | '2'
): T[] => {
  // 构建币种映射表，避免多次遍历
  //desc => key
  const currencyMap_1 = new Map<string, string>();
  currencys.forEach(c => {
    currencyMap_1.set(c.desc, c.key);
  });
  //key => desc
  const currencyMap_2 = new Map<string, string>();
  currencys.forEach(c => {
    currencyMap_2.set(c.key, c.desc);
  });
  let currencyMap = type === '1' ? currencyMap_1 : currencyMap_2;

  return data.map(item => ({
    ...item,
    currency: currencyMap.get(item?.currency ?? '') || item.currency,
  }));
};

export const generatePriceTable = (t: TFunction, data: SupplierPrice[], excelName?: string) => {
  // 工作表数据（仅表头）
  const worksheetData = [
    [
      t("product.code"),
      t("product.name"),
      t("order.brand"),
      t("product.uom"),
      t("purchase.brand"),
      t("purchase.mpn"),
      t("purchase.dcc"),
      t("purchase.netWeight"),
      t("purchase.grossWeight"),
      t("purchase.stPrice"),
      t("purchase.mpq"),
      t("purchase.days"),
      t("purchase.taxRate"),
      t("purchase.unRatePrice"),
      t("product.quantity"),
      t("purchase.effectiveAt"),
      t("purchase.expiredAtEcl"),
      t("purchase.currency"),
      t("purchase.remark"),
      t("purchase.code"),
      t("purchase.name"),
      t("purchase.shortName"),
      t("purchase.spareRate"),
    ]
  ];
  data.forEach(item => {
    let taxRate = item.taxRate || item.supplier?.taxRate;
    let unRatePrice = taxRate ? priceFormat(noRateAmount(item.price ?? "", divided(taxRate, "100"))) : ""

    worksheetData.push([
      item.product?.code ?? "",
      item.product?.name ?? "",
      item.product?.model ?? "",
      item.product?.uom?.toLocaleUpperCase() ?? "",
      item.brand ?? "",
      item?.mpn ?? "",
      item.dcc ?? "",
      item.netWeight ?? "",
      item.grossWeight ?? "",
      priceFormat(item.price ?? ""),
      item.mpq ?? '',
      item.days?.toString() ?? '',
      (item.taxRate ?? item.supplier?.taxRate) ? `${item.taxRate ?? item.supplier?.taxRate}%` : '',
      unRatePrice,
      '',
      item.effectiveAt ? dateFormat(dayjs(item.effectiveAt)) : '',
      item.expiredAt ? dateFormat(dayjs(item.expiredAt)) : '',
      item.currency,
      item.remark ?? "",
      item?.supplier?.code ?? "",
      item?.supplier?.name ?? "",
      item?.supplier?.shortName ?? "",
      item.spareRate ? `${item.spareRate}%` : '',
    ])
    if (item.ladders && item.ladders.length > 1) {
      item.ladders.filter(ladder => ladder.count > 1)
        .sort((a, b) => b.count - a.count)
        .forEach(ladder => {
          worksheetData.push([
            item.product?.code ?? "",
            item.product?.name ?? "",
            item.product?.model ?? "",
            item.product?.uom?.toLocaleUpperCase() ?? "",
            item.brand ?? "",
            item?.mpn ?? "",
            item.dcc ?? "",
            item.netWeight ?? "",
            item.grossWeight ?? "",
            priceFormat(ladder.price ?? ""),
            item.mpq ?? '',
            item.days?.toString() ?? '',
            (item.taxRate ?? item.supplier?.taxRate) ? `${item.taxRate ?? item.supplier?.taxRate}%` : '',
            '',
            ladder.count + '',
            item.effectiveAt ? dateFormat(dayjs(item.effectiveAt)) : '',
            item.expiredAt ? dateFormat(dayjs(item.expiredAt)) : '',
            item.currency,
            "",
            item?.supplier?.code ?? "",
            item?.supplier?.name ?? "",
            item?.supplier?.shortName ?? "",
            '',
          ])
        })
    }
  })

  // 创建工作表
  const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);

  // 设置列宽
  worksheet['!cols'] = [
    { wch: 20 },  // 供应商编码
    { wch: 20 },  // 存货编码
    { wch: 26 },  // 存货名称
    { wch: 45 },  // 存货型号
    { wch: 15 },  // 含税单价
    { wch: 15 },  // 物料有效期(年)
    { wch: 15 },  // 供货周期(天)
    { wch: 15 },  // 净重
    { wch: 15 },  // 毛重
    { wch: 12 },  // 最小包装
    { wch: 12 },  // 数量
    { wch: 12 },  // 结算币种
    { wch: 15 },  // 品牌
    { wch: 15 },  // 原厂代码
    { wch: 15 },  // 保质期（年）
  ];

  // todo:数据写入

  // 创建工作簿
  const workbook = XLSX.utils.book_new();
  const sheetName = excelName || t('purchase.supplierHisPrice');
  XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);

  // 生成Excel文件并下载
  const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
  const dataBlob = new Blob([excelBuffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8',
  });

  const fileName = excelName || `${t('purchase.supplierHisPrice')}.xlsx`;
  saveAs(dataBlob, fileName);

}
